import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import LoaderAction from 'yoda-site-components/lib/actions/LoadingAction';
import { getSelectedColor } from 'yoda-site-components/lib/selectors/ProductOptionsSelector';
import TokenProvider from 'yoda-core-components/lib/helpers/TokenProvider/TokenProvider';
import User from 'yoda-site-components/lib/helpers/User/User';
import { ACCOUNT_ID } from 'yoda-site-components/lib/common/Constants';
import {
    selectPreferences,
    selectFeatureFlags,
} from 'yoda-site-components/lib/selectors/ContextSelector';
import {
    miniPDPOnShopTheLookOpen,
    miniPDPOnShopTheLookClose,
    miniPDPOnClose,
    miniPdpAtcFromSTL,
} from 'yoda-site-components/lib/actions/MiniPDPAction';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { updateLastSelectedColor } from 'yoda-site-components/lib/actions/ShopTheLookAction';
import {
    shopTheLookLoadSuccess,
    shopThisLookClickEvent,
    shopTheLookMiniPDPClickEvent,
} from '../../actions/AnalyticsAction';
import ProductConstants from '../../helpers/ProductConstants';
import * as styles from './ShopTheLook.css';

const cx = classNames.bind(styles);

export class ShopTheLook extends Component {
    static propTypes = {
        productDetails: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        productPrices: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        selectedColor: PropTypes.string,
        lastSelectedColor: PropTypes.string,
        outfitsData: PropTypes.func,
        jumpLink: PropTypes.func,
        actions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        preferences: PropTypes.oneOfType([PropTypes.object]),
        miniPDP: PropTypes.oneOfType([PropTypes.object]),
        featureFlags: PropTypes.object,
    };

    static defaultProps = {
        productDetails: {},
        productPrices: [],
        selectedColor: '',
        lastSelectedColor: '',
        outfitsData: null,
        jumpLink: null,
        actions: {},
        preferences: {},
        miniPDP: {},
        featureFlags: {},
    };

    constructor(props) {
        super(props);
        this.styliticsWidgetObj = null;
        this.state = {
            scriptLoaded: false,
            outfitsFromWidget: [],
            initialLoadEmptyOutfits: false,
            hideLoader: false,
            jumpLinkImage: null,
        };
        this.stylisticsRef = React.createRef();
        this.jumpLinkImg = null;
    }

    /* istanbul ignore next */
    observer =
        !__SERVER__ &&
        new MutationObserver((mutationsList, observer) => {
            this.jumpLinkImg = this.stylisticsRef.current.querySelectorAll(
                `.stylitics-bundle-container .stylitics-collage-container .stylitics-main-image`
            );
            this.setState({ jumpLinkImage: this.jumpLinkImg });
            observer.disconnect();
        });

    componentDidMount() {
        this.loadScript();
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
        const {
            miniPDP: { miniPDPSTLATC = false },
            actions,
            featureFlags,
        } = this.props;

        // TOFPMC-2853 - prevent rerunning logic if props have not changed
        const newMiniPDPState = nextProps?.miniPDP?.miniPDPSTL;
        const currentMiniPDPState = this.props?.miniPDP?.miniPDPSTL;
        if (newMiniPDPState !== currentMiniPDPState) {
            window.history.pushState(null, null, window.location.href);
            if (newMiniPDPState === 'open') {
                window.onpopstate = function () {
                    actions.miniPDPOnClose();
                };
            }
            if (newMiniPDPState === 'close') {
                window.onpopstate = function () {
                    window.history.back();
                };
            }
        }

        if (nextProps.miniPDP && nextProps.miniPDP.miniPDPSTLATC && !miniPDPSTLATC) {
            // To trigger stylitics analytics method when add to cart CTA is clicked from mini PDP
            const itemId = get(nextProps, 'miniPDP.itemIdSTL', '');
            !isEmpty(itemId) && this.styliticsWidgetObj.trackAddItemToCart(itemId);
            // To trigger miniPdpAtcFromSTL action to enable next add to cart.
            actions.miniPdpAtcFromSTL && actions.miniPdpAtcFromSTL(false);
        }
        /* istanbul ignore next */
        !featureFlags.enableStyliticsDimension &&
            Object.keys(this.state.outfitsFromWidget).length > 0 &&
            this.observer.observe(document, {
                childList: true,
                subtree: true,
            });
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { selectedColor, productPrices } = this.props;
        const { outfitsFromWidget, hideLoader, jumpLinkImage } = this.state;

        if (selectedColor && selectedColor !== nextProps.selectedColor) {
            return true;
        }

        if (nextState.outfitsFromWidget !== outfitsFromWidget) {
            this.isSTSWidgetLoaded() &&
                this.isSTSWidgetLoaded().length === 0 &&
                this.props.outfitsData(nextState.outfitsFromWidget);
            return true;
        }

        if (nextState.jumpLinkImage !== jumpLinkImage) {
            this.isSTSWidgetLoaded() &&
                this.isSTSWidgetLoaded().length === 0 &&
                this.props.jumpLink(nextState.jumpLinkImage);
            return true;
        }

        if (nextState.hideLoader !== hideLoader) {
            return true;
        }
        if (nextProps.productPrices !== productPrices) {
            return true;
        }

        return false;
    }

    componentDidUpdate(prevProps) {
        /* istanbul ignore next */
        if (
            this.isWidgetLoaded() &&
            this.props.selectedColor &&
            this.props.selectedColor !== prevProps.selectedColor
        ) {
            this.refreshContent();
        }
    }

    isWidgetLoaded = () => document.getElementsByClassName('stylitics-classic-base');

    isSTSWidgetLoaded = () => document.getElementsByClassName('stylitics-ms-widget-root');

    loadScript = () => {
        const { preferences, featureFlags } = this.props;
        const { enableStyliticsDimension = false } = featureFlags;
        const stylisticsURL = get(preferences, 'shopTheLook.stlScriptURL', '');
        const stlScriptURLDimensionsUpgrade = get(
            preferences,
            'shopTheLook.stlScriptURLDimensionsUpgrade',
            ''
        );
        const stylisticsProps = {
            src: enableStyliticsDimension ? stlScriptURLDimensionsUpgrade : stylisticsURL,
            async: true,
            defer: false,
            onSuccess: () => {
                /* istanbul ignore next */
                this.initiateShopTheLookFeature();
            },
        };
        ScriptLoader.load(stylisticsProps);
        this.setState({
            scriptLoaded: true,
        });
    };

    getOptionsForShopTheLook = () => {
        const { featureFlags } = this.props;
        const profileID = User.isUserLoggedIn(true) ? TokenProvider.get(ACCOUNT_ID) : 'NA';
        const { enableStyliticsDimension = false } = featureFlags;
        const opts = {
            api: {
                min: 3,
                max: 6,
                item_number: this.getItemNumber(this.props.selectedColor),
            },
            text: {
                viewDetailsCTA: enableStyliticsDimension
                    ? ProductConstants.SHOP_THE_LOOK
                    : ProductConstants.SHOP_THIS_LOOK,
                itemLinkCTA: enableStyliticsDimension
                    ? ProductConstants.SHOP_ATC
                    : ProductConstants.ADD_TO_CART,
            },
            customer: {
                identifier: 'jcpenney',
                profileId: profileID,
            },
            price: {
                salesPriceStyle: 'sales-price-only',
            },
            display: {
                enableDimensionUI: !!enableStyliticsDimension,
            },
        };
        return opts;
    };

    getItemNumber = (selectedColor) => {
        const { productDetails } = this.props;
        const selectedColorName = selectedColor || productDetails.ppFeatureColor;
        const itemNumber = selectedColorName
            ? `${productDetails.id}_${selectedColorName.replaceAll(' ', '-').toLowerCase()}`
            : '';
        return itemNumber;
    };

    isCollection = () => {
        const collections = get(this.props.productDetails, 'collection', []);
        const collectionItem = collections && Object.keys(collections).length > 0;
        return collectionItem;
    };

    constructPPid = (bundleOutfits) => {
        const productIds = [];
        if (bundleOutfits) {
            bundleOutfits.forEach((subelement) => {
                productIds.push(subelement.remote_id.split('_')[0]);
            });
        }
        return productIds;
    };

    refreshJumplink = (data = false) => {
        const jumplinkElement = document.getElementById('stylitics-jumplink');
        const titleElement = document.getElementById('widget-title');
        /* istanbul ignore next */
        if (jumplinkElement && titleElement && !data) {
            jumplinkElement.style.display = 'block';
            titleElement.style.display = 'block';
        } else if (data) {
            if (this.isSTSWidgetLoaded().length === 0) {
                jumplinkElement.style.display = 'none';
            }
            titleElement.style.display = 'none';
        }
    };

    // eslint-disable-next-line class-methods-use-this
    itemPricingOverride = (data) => {
        if (!data || !data.item || !data.element) return;

        const { item } = data;
        if (item.promotions && item.promotions.includes('with_code')) {
            const text = document.createElement('span');
            text.classList.add('couponText');
            text.style.marginLeft = '2px';
            text.innerHTML = ' with code';
            const priceContainer = data.element.querySelector('.stylitics-ui-price');
            if (priceContainer) {
                const salePrice = priceContainer.querySelector('.stylitics-ui-regular-price');
                if (salePrice) {
                    salePrice.classList.add('priceText');
                    salePrice.append(text);
                }
            }
        }
    };

    /* istanbul ignore next */
    getItemCtaLinkText = (itemName, productId) => {
        const ref = this.stylisticsRef?.current;
        const liCtaSpan = itemName
            ? ref
                  ?.querySelector(`li.stylitics-outfit-item [aria-label="${itemName}"]`)
                  ?.closest('li.stylitics-outfit-item')
                  ?.querySelector('.stylitics-cta-text.stylitics-outfit-list-item-cta-button-text')
            : null;
        const anchorCtaSpan = ref
            ?.querySelector(`a.item-cta[href*="${productId}"]`)
            ?.querySelector('.stylitics-cta-text:not(.stylitics-outfit-list-item-cta-button-text)');
        return liCtaSpan?.textContent?.trim() || anchorCtaSpan?.textContent?.trim() || 'Shop';
    };

    /* istanbul ignore next */
    getLookCtaLinkText = (linkPosition) => {
        const allCtaSpans = this.stylisticsRef?.current?.querySelectorAll('.stylitics-cta-text');
        const lookCtaSpans = [...(allCtaSpans || [])].filter(
            (el) => el.className.trim() === 'stylitics-cta-text'
        );
        return lookCtaSpans[linkPosition]?.textContent?.trim() || '';
    };

    /* istanbul ignore next */
    initiateShopTheLookFeature = () => {
        const { scriptLoaded } = this.state;
        const { actions, featureFlags } = this.props;
        const { enableStyliticsDimension = false } = featureFlags;
        /* istanbul ignore next */
        if (scriptLoaded) {
            let selectPrevColor = '';
            const opts = this.getOptionsForShopTheLook();
            this.styliticsWidgetObj = enableStyliticsDimension
                ? new StyliticsDimensionCollageWidget('jcpenney', 'shopLookContainer', opts)
                : new StyliticsClassicWidget('jcpenney', 'shopLookContainer', opts);
            this.styliticsWidgetObj.on(
                'mount',
                enableStyliticsDimension ? 'outfits' : 'bundles',
                (data) => {
                    this.setState({
                        hideLoader: true,
                    });
                    if (Object.keys(data.bundles).length > 0) {
                        this.refreshJumplink();
                        window && actions.shopTheLookLoadSuccess(window.digitalData);
                        this.setState({
                            outfitsFromWidget: data.bundles,
                        });
                        actions.updateLastSelectedColor(this.props.selectedColor);
                        const selectedColorData = get(data, 'bundles[0].primary_remote_id', '');
                        selectPrevColor = selectedColorData && selectedColorData.split('_')[1];
                    } else {
                        !data.hidden &&
                            selectPrevColor !== '' &&
                            this.refreshContent(selectPrevColor);
                        this.refreshJumplink(data.hidden);
                        this.setState({
                            initialLoadEmptyOutfits: true,
                            outfitsFromWidget: [],
                        });
                    }
                }
            );

            this.styliticsWidgetObj.on('mount', 'item', (data) => this.itemPricingOverride(data));

            this.styliticsWidgetObj.override('click', 'item', (data) => {
                const item = data?.item;
                const bundle = enableStyliticsDimension ? data?.outfit : data?.bundle;
                const position = (bundle?.items || []).findIndex(
                    (i) => i.item_id === item?.item_id
                );
                const linkPosition = position >= 0 ? position : 0;
                const productId = get(item, 'remote_id', '').split('_')[0];
                const itemName = get(item, 'name', '');
                const linkText = this.getItemCtaLinkText(itemName, productId);
                this.onShopTheLookItemClick(item, bundle, linkPosition, linkText);
            });

            this.styliticsWidgetObj.on(
                'click',
                enableStyliticsDimension ? 'outfit' : 'bundle',
                (data) => {
                    if (Object.keys(data).length > 0) {
                        const bundle = data?.bundle || data?.outfit;
                        const shopTheLookID = get(data, 'bundle.id', '').toString();
                        const bundlePosition = (this.state.outfitsFromWidget || []).findIndex(
                            (b) => b.id && b.id.toString() === shopTheLookID
                        );
                        !isEmpty(shopTheLookID) &&
                            actions.shopThisLookClickEvent({
                                moduleName: ProductConstants.SHOP_THE_LOOK.toLowerCase(),
                                shopTheLookID,
                                linkText: this.getLookCtaLinkText(
                                    bundlePosition >= 0 ? bundlePosition : 0
                                ),
                                linkPosition: bundlePosition >= 0 ? bundlePosition : 0,
                            });
                    }
                }
            );

            this.styliticsWidgetObj.start();
        }
    };

    onShopTheLookItemClick = (ppId, bundle, position = 0, linkText = '') => {
        /* istanbul ignore next */
        const skuData = get(ppId, 'remote_id', '');
        const itemId = get(ppId, 'item_id', '');
        const { actions } = this.props;
        const skuID =
            (ppId &&
                get(ppId, 'other_client_item_ids.sku_nbrs', '') &&
                !isEmpty(ppId.other_client_item_ids.sku_nbrs) &&
                ppId.other_client_item_ids.sku_nbrs[0]) ||
            '';
        /* istanbul ignore next */
        if (!isEmpty(skuData)) {
            const options = {
                ppId: skuData.split('_')[0],
                skuId: skuID,
            };
            const customEvent = document.createEvent('CustomEvent');
            customEvent.initCustomEvent('miniPDP.open', true, true, options);
            document.dispatchEvent(customEvent);
            actions.miniPDPOnShopTheLookOpen(itemId);
            actions.shopTheLookMiniPDPClickEvent &&
                actions.shopTheLookMiniPDPClickEvent({
                    moduleName: ProductConstants.SHOP_THE_LOOK.toLowerCase(),
                    shopTheLookID: (bundle.id && bundle.id.toString()) || '',
                    shopTheLookPPID: options.ppId,
                    linkText,
                    linkPosition: position,
                });
            // To trigger stylitics analytics method when add to cart CTA is clicked which is present with in widget.
            this.styliticsWidgetObj.trackClickItem(skuData);
        }
    };

    refreshContent = (color = this.props.selectedColor) => {
        const { scriptLoaded } = this.state;
        /* istanbul ignore next */
        if (scriptLoaded && this.styliticsWidgetObj) {
            const itemNumber = this.getItemNumber(color);
            this.styliticsWidgetObj.refresh({ api: { item_number: itemNumber } });
            this.refreshJumplink();
        }
    };

    stylisticsMarkup = () => {
        const stylistics = `<style>
        .stylitics-classic-base {
            font-family: 'OPEN SANS';
        }
        .stylitics-dimension-collage-base * {
            font-family: 'OPEN SANS' !important;
            font-size: 14px !important;
            line-height: 20px !important;
        }
        .stylitics-beauty-widget-base * {
            font-family: 'OPEN SANS' !important;
            font-size: 14px !important;
            line-height: 20px !important;
        }
        .stylitics-ui-item-info-name {
            text-align: left !important;
        }
        .stylitics-dimension-collage-card {
            background-color: white !important;
        }
        .stylitics-dimension-collage-item-name {
            font-family: Arial !important;
        }
        .stylitics-dimension-collage-card-footer {
            font-weight: 600 !important;
        }
        .stylitics-dimension-collage-item-cta {
            font-family: Arial !important;
        }
        .stylitics-dimension-collage-modal-heading {
            font-size: 16px !important;
            line-height: 28px !important;
            font-weight: 400 !important;
        }
        .stylitics-container .stylitics-card {
            width: calc((100% + 48px) / 2.275 - 16px) !important;
        }
        .stylitics-modal-scroll > .stylitics-modal {
            z-index: 99999991 !important;
        }
        .stylitics-item-regular-price {
            font-family: 'OPEN SANS';
            font-weight: 700 !important;
        }
        .couponText{
            font-style: italic !important;
            font-weight: 400 !important;
        }
        .priceText{
            color: #E71324 !important;
        }
        @media only screen and (max-width: 600px) {
            .stylitics-container .stylitics-card {
                width: calc((100% + 48px) / 1.275 - 16px) !important;
            }
        }​
        </style>`;
        return stylistics;
    };

    loadContent = () => {
        const { outfitsFromWidget, hideLoader } = this.state;
        const shopTheLookBlock = (
            <div>
                {!hideLoader && (
                    <div
                        className={dt([
                            'absolute',
                            'h-[400px]',
                            'left-0',
                            'right-0',
                            'z-[1]',
                            'm-auto',
                            'mt-4',
                        ])}
                    >
                        <Loader />
                    </div>
                )}

                <h4
                    id="widget-title"
                    className={dt([
                        'text-center',
                        'font-open-sans-semibold',
                        '!leading-8',
                        'lg:text-xLg',
                        'sm:text-lg',
                        'pb-3',
                        'hidden',
                    ])}
                >
                    {ProductConstants.SHOP_THE_LOOK}
                </h4>
                <div
                    id="shopLookContainer"
                    className={`${cx('shopTheLookContainer')} ${dt([
                        'text-center',
                        'font-open-sans-semibold',
                        '!text-base',
                        'leading-7',
                        'mb-10',
                    ])}`}
                    dangerouslySetInnerHTML={{ __html: this.stylisticsMarkup() }}
                    ref={this.stylisticsRef}
                />
            </div>
        );
        return shopTheLookBlock;
    };

    render() {
        return <div>{this.loadContent()}</div>;
    }
}

const mapStateToProps = (state) => ({
    preferences: selectPreferences(state),
    selectedColor: getSelectedColor(false)(state),
    productPrices: state.shopTheLook ? state.shopTheLook.productPrices : {},
    lastSelectedColor: state.shopTheLook ? state.shopTheLook.lastSelectedColor : '',
    miniPDP: state.miniPDP ? state.miniPDP : {},
    featureFlags: selectFeatureFlags(state),
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            ...LoaderAction,
            updateLastSelectedColor,
            shopTheLookLoadSuccess,
            shopThisLookClickEvent,
            shopTheLookMiniPDPClickEvent,
            miniPDPOnShopTheLookOpen,
            miniPDPOnShopTheLookClose,
            miniPDPOnClose,
            miniPdpAtcFromSTL,
        },
        dispatch
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(ShopTheLook);
